import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, api } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { 
  Activity, 
  ArrowLeft,
  User,
  Bell,
  Shield,
  Download,
  Trash2,
  AlertTriangle
} from 'lucide-react';
import { FigIcon } from '../components/FigIcon';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';

const SettingsPage = () => {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    age: user?.age || '',
    timezone: user?.timezone || 'UTC'
  });
  const [reminderSettings, setReminderSettings] = useState({
    enabled: user?.reminder_settings?.enabled || false,
    day_of_week: user?.reminder_settings?.day_of_week || 0,
    time_of_day: user?.reminder_settings?.time_of_day || '09:00'
  });

  const handleProfileUpdate = async () => {
    setLoading(true);
    try {
      await api.put('/auth/profile', {
        name: profileData.name || null,
        age: profileData.age ? parseInt(profileData.age) : null,
        timezone: profileData.timezone
      });
      await refreshUser();
      toast.success('Profile updated');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleReminderUpdate = async () => {
    setLoading(true);
    try {
      await api.put('/auth/reminder-settings', reminderSettings);
      toast.success('Reminder settings updated');
    } catch (error) {
      toast.error('Failed to update reminders');
    } finally {
      setLoading(false);
    }
  };

  const handleExportData = async () => {
    try {
      const response = await api.get('/gdpr/export');
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `trajectory-data-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Data exported successfully');
    } catch (error) {
      toast.error('Failed to export data');
    }
  };

  const handleDeleteAccount = async () => {
    try {
      await api.delete('/gdpr/delete');
      logout();
      toast.success('Account deleted');
      navigate('/');
    } catch (error) {
      toast.error('Failed to delete account');
    }
  };

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  return (
    <div className="min-h-screen bg-[#E8E4DC]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-[#525252] hover:text-[#1A1C19]">
            <ArrowLeft className="w-4 h-4" />
            Dashboard
          </Link>
          <div className="flex items-center gap-2">
            <FigIcon className="w-6 h-6 text-[#2F3E30]" />
            <span className="font-bold text-lg" style={{ fontFamily: 'Libre Baskerville, serif' }}>SETTINGS</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-6">
        <div className="max-w-2xl mx-auto space-y-8">
          {/* Profile Section */}
          <div className="bg-[#EBEBE6] border border-[#D1D1CD] p-6">
            <div className="flex items-center gap-3 mb-6">
              <User className="w-5 h-5 text-[#2F3E30]" />
              <h2 className="font-bold text-xl" style={{ fontFamily: 'Libre Baskerville, serif' }}>PROFILE</h2>
            </div>
            <div className="space-y-4">
              <div>
                <Label className="text-sm">Email</Label>
                <Input
                  value={user?.email || ''}
                  disabled
                  className="mt-2 bg-[#E8E4DC] border-[#D1D1CD] h-12 opacity-60"
                />
              </div>
              <div>
                <Label className="text-sm">Name</Label>
                <Input
                  value={profileData.name}
                  onChange={(e) => setProfileData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Your name"
                  className="mt-2 bg-[#EBEBE6] border-[#D1D1CD] h-12"
                  data-testid="settings-name-input"
                />
              </div>
              <div>
                <Label className="text-sm">Age</Label>
                <Input
                  type="number"
                  value={profileData.age}
                  onChange={(e) => setProfileData(prev => ({ ...prev, age: e.target.value }))}
                  placeholder="45"
                  className="mt-2 bg-[#EBEBE6] border-[#D1D1CD] h-12"
                  data-testid="settings-age-input"
                />
              </div>
              <div>
                <Label className="text-sm">Timezone</Label>
                <Select
                  value={profileData.timezone}
                  onValueChange={(value) => setProfileData(prev => ({ ...prev, timezone: value }))}
                >
                  <SelectTrigger className="mt-2 bg-[#EBEBE6] border-[#D1D1CD] h-12" data-testid="timezone-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="UTC">UTC</SelectItem>
                    <SelectItem value="Europe/London">London (GMT/BST)</SelectItem>
                    <SelectItem value="Europe/Paris">Paris (CET)</SelectItem>
                    <SelectItem value="America/New_York">New York (EST)</SelectItem>
                    <SelectItem value="America/Los_Angeles">Los Angeles (PST)</SelectItem>
                    <SelectItem value="Asia/Tokyo">Tokyo (JST)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button 
                onClick={handleProfileUpdate}
                disabled={loading}
                className="bg-[#2F3E30] hover:bg-[#3A4D3B]"
                data-testid="save-profile-btn"
              >
                Save Profile
              </Button>
            </div>
          </div>

          {/* Reminders Section */}
          <div className="bg-[#EBEBE6] border border-[#D1D1CD] p-6">
            <div className="flex items-center gap-3 mb-6">
              <Bell className="w-5 h-5 text-[#F59E0B]" />
              <h2 className="font-bold text-xl" style={{ fontFamily: 'Libre Baskerville, serif' }}>REMINDER SETTINGS</h2>
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg bg-[#E8E4DC]">
                <div>
                  <p className="font-medium">Weekly Check-in Reminder</p>
                  <p className="text-sm text-[#525252]">Get an email reminder for your weekly check-in</p>
                </div>
                <Switch
                  checked={reminderSettings.enabled}
                  onCheckedChange={(checked) => setReminderSettings(prev => ({ ...prev, enabled: checked }))}
                  data-testid="reminder-switch"
                />
              </div>
              {reminderSettings.enabled && (
                <>
                  <div>
                    <Label className="text-sm">Reminder Day</Label>
                    <Select
                      value={String(reminderSettings.day_of_week)}
                      onValueChange={(value) => setReminderSettings(prev => ({ ...prev, day_of_week: parseInt(value) }))}
                    >
                      <SelectTrigger className="mt-2 bg-[#EBEBE6] border-[#D1D1CD] h-12">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {days.map((day, index) => (
                          <SelectItem key={index} value={String(index)}>{day}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm">Reminder Time</Label>
                    <Input
                      type="time"
                      value={reminderSettings.time_of_day}
                      onChange={(e) => setReminderSettings(prev => ({ ...prev, time_of_day: e.target.value }))}
                      className="mt-2 bg-[#EBEBE6] border-[#D1D1CD] h-12"
                    />
                  </div>
                </>
              )}
              <Button 
                onClick={handleReminderUpdate}
                disabled={loading}
                className="bg-[#2F3E30] hover:bg-[#3A4D3B]"
                data-testid="save-reminders-btn"
              >
                Save Reminder Settings
              </Button>
            </div>
          </div>

          {/* Data & Privacy Section */}
          <div className="bg-[#EBEBE6] border border-[#D1D1CD] p-6">
            <div className="flex items-center gap-3 mb-6">
              <Shield className="w-5 h-5 text-[#10B981]" />
              <h2 className="font-bold text-xl" style={{ fontFamily: 'Libre Baskerville, serif' }}>DATA & PRIVACY</h2>
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg bg-[#E8E4DC]">
                <div>
                  <p className="font-medium">Export My Data</p>
                  <p className="text-sm text-[#525252]">Download all your data as a JSON file</p>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleExportData}
                  className="border-[#D1D1CD]"
                  data-testid="export-data-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
              </div>
              <div className="flex items-center justify-between p-4 rounded-lg bg-[#EF4444]/10 border border-[#EF4444]/20">
                <div>
                  <p className="font-medium text-[#EF4444]">Delete Account</p>
                  <p className="text-sm text-[#525252]">Permanently delete your account and all data</p>
                </div>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button 
                      variant="destructive" 
                      size="sm"
                      data-testid="delete-account-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-[#EBEBE6] border-[#D1D1CD]">
                    <AlertDialogHeader>
                      <AlertDialogTitle className="flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-[#EF4444]" />
                        Delete Account?
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        This action cannot be undone. This will permanently delete your account and 
                        remove all your data including questionnaire responses, scores, and check-in history.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="bg-[#D1D1CD] border-[#3f3f46] hover:bg-[#3f3f46]">Cancel</AlertDialogCancel>
                      <AlertDialogAction 
                        onClick={handleDeleteAccount}
                        className="bg-[#EF4444] hover:bg-[#DC2626]"
                      >
                        Yes, delete my account
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          </div>

          {/* Account Info */}
          <div className="bg-[#EBEBE6] border border-[#D1D1CD] p-6">
            <h3 className="font-bold mb-4" style={{ fontFamily: 'Libre Baskerville, serif' }}>ACCOUNT STATUS</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-[#525252]">Member since</span>
                <span>{user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#525252]">Payment status</span>
                <span className={user?.is_paid ? 'text-[#10B981]' : 'text-[#F59E0B]'}>
                  {user?.is_paid ? 'Active' : 'Free'}
                </span>
              </div>
              {user?.payment_type && (
                <div className="flex justify-between">
                  <span className="text-[#525252]">Plan type</span>
                  <span>{user.payment_type === 'subscription' ? '3-Month Subscription' : 'One-Time Access'}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SettingsPage;
